import React, { useRef, useEffect, useState } from "react";
import { ContextualMenu, Icon, IconButton, TooltipHost, ITooltipHostStyles } from "@fluentui/react";
import FileChip from "./FileChip";
import { logEvent } from "../../../../helper/AppInsightsHelper";

interface ChatInputProps {
    input: string;
    loading: boolean;
    attachedFile: File | null;
    attachedImages: File[];
    isRecording: boolean;
    isSpeechSupported: boolean;
    onInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onRemoveFile: () => void;
    onRemoveImage: (index: number) => void;
    onSend: () => void;
    onStop: () => void;
    onStartRecording: () => void;
    onStopRecording: () => void;
}

const tooltipStyles: Partial<ITooltipHostStyles> = {
    root: { display: "inline-block" },
};

const ChatInput: React.FC<ChatInputProps> = ({
    input,
    loading,
    attachedFile,
    attachedImages,
    isRecording,
    isSpeechSupported,
    onInputChange,
    onKeyDown,
    onFileChange,
    onImageChange,
    onRemoveFile,
    onRemoveImage,
    onSend,
    onStop,
    onStartRecording,
    onStopRecording,
}) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const attachButtonRef = useRef<HTMLButtonElement>(null);
    const [isAttachMenuOpen, setIsAttachMenuOpen] = useState(false);

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onInputChange(e);
        // Auto-adjust textarea height
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
        }
    };

    // Auto-adjust textarea height when input changes externally (e.g., speech recognition)
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
        }
    }, [input]);

    return (
        <div
            style={{
                width: "100%",
                maxWidth: "100%",
                display: "flex",
                alignItems: "center",
                borderRadius: "25px",
                padding: "12px 15px",
                background: "#fff",
                boxShadow: "0 1px 5px rgba(0,0,0,0.1)",
            }}
        >
            {/* Hidden file input */}
            <input
                type="file"
                id="fileInput"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={onFileChange}
                accept=".txt,text/plain"
            />
            <input
                type="file"
                id="imageInput"
                ref={imageInputRef}
                style={{ display: "none" }}
                onChange={onImageChange}
                accept="image/*"
                multiple
            />

            <TooltipHost
                content="Attach"
                calloutProps={{
                    styles: {
                        calloutMain: {
                            color: "white",
                            borderRadius: "8px",
                            padding: "6px 12px",
                            fontSize: "13px",
                            fontWeight: 500,
                            display: "inline-block",
                            width: "auto",
                            maxWidth: "300px",
                            whiteSpace: "nowrap",
                        },
                    },
                }}
                styles={tooltipStyles}
            >
                <button
                    ref={attachButtonRef}
                    onClick={() => setIsAttachMenuOpen((prev) => !prev)}
                    style={{
                        background: "#f3f2f1",
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontSize: "20px",
                        marginRight: "10px",
                        color: "#0078D7",
                        padding: "6px 10px",
                        transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#e5f1fb")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "#f3f2f1")}
                >
                    <Icon iconName="Attach" />
                </button>
            </TooltipHost>

            {isAttachMenuOpen && (
                <ContextualMenu
                    target={attachButtonRef.current}
                    onDismiss={() => setIsAttachMenuOpen(false)}
                    items={[
                        {
                            key: "attach-text",
                            text: "Text file",
                            iconProps: { iconName: "Attach" },
                            onClick: () => {
                                logEvent("AttachmentButtonInChat_Clicked", "AI Hub - Copilot Chat", "ChatInput", {
                                    attachmentType: "text"
                                });
                                setIsAttachMenuOpen(false);
                                fileInputRef.current?.click();
                            }
                        },
                        {
                            key: "attach-image",
                            text: "Image",
                            iconProps: { iconName: "Photo2" },
                            onClick: () => {
                                logEvent("AttachmentButtonInChat_Clicked", "AI Hub - Copilot Chat", "ChatInput", {
                                    attachmentType: "image"
                                });
                                setIsAttachMenuOpen(false);
                                imageInputRef.current?.click();
                            }
                        }
                    ]}
                />
            )}

            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    borderRadius: "25px",
                    padding: "8px 12px",
                    background: "#fff",
                    flex: 1,
                    minHeight: "40px",
                    flexWrap: "wrap",
                }}
            >
                {attachedFile && <FileChip file={attachedFile} onRemove={onRemoveFile} />}
                {attachedImages.map((image, index) => (
                    <FileChip key={`${image.name}-${index}`} file={image} onRemove={() => onRemoveImage(index)} />
                ))}

                <textarea
                    ref={textareaRef}
                    placeholder={attachedFile || attachedImages.length > 0 ? "" : "Type a message or attach file/image..."}
                    value={String(input || "")}
                    onChange={handleInputChange}
                    onKeyDown={onKeyDown}
                    disabled={loading}
                    rows={1}
                    style={{
                        flex: 1,
                        border: "none",
                        outline: "none",
                        fontSize: "16px",
                        resize: "none",
                        overflow: "hidden",
                        background: "transparent",
                        minWidth: "60px",
                        fontFamily:
                            "\"Segoe UI\", \"Segoe UI Web (West European)\", \"Segoe UI\", -apple-system, BlinkMacSystemFont, Roboto, \"Helvetica Neue\", sans-serif",
                    }}
                />
            </div>

            <style>
                {`
textarea::placeholder {
    font-family: "Segoe UI", "Segoe UI Web (West European)", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif;
    color: #999;
}
`}
            </style>

            <TooltipHost
                content={
                    isSpeechSupported
                        ? isRecording
                            ? "Stop Recording"
                            : "Start Recording"
                        : "Voice input not supported"
                }
                calloutProps={{
                    styles: {
                        calloutMain: {
                            color: "white",
                            borderRadius: "8px",
                            padding: "6px 12px",
                            fontSize: "13px",
                            fontWeight: 500,
                            display: "inline-block",
                            width: "auto",
                            maxWidth: "300px",
                            whiteSpace: "nowrap",
                        },
                    },
                }}
                styles={tooltipStyles}
            >
                <IconButton
                    iconProps={{ iconName: "Microphone" }}
                    ariaLabel="Voice Input"
                    disabled={!isSpeechSupported}
                    onClick={() => {
                        if (!isSpeechSupported) return;
                        if (isRecording) {
                            onStopRecording();
                        } else {
                            // Log start recording
                            logEvent("VoiceInput_StartedRecording", "AI Hub - Copilot Chat", "ChatInput");
                            onStartRecording();
                        }
                    }}
                    style={{
                        color: !isSpeechSupported ? "gray" : isRecording ? "#D9534F" : "#0078D7",
                        marginRight: "6px",
                    }}
                />
            </TooltipHost>

            <button
                onClick={loading ? onStop : onSend}
                disabled={(!String(input || "").trim() && !attachedFile && attachedImages.length === 0) || loading}
                style={{
                    background: "transparent",
                    border: "none",
                    cursor: (!String(input || "").trim() && !attachedFile && attachedImages.length === 0) || loading ? "not-allowed" : "pointer",
                    fontSize: "20px",
                    marginLeft: "10px",
                    color: loading ? "#D9534F" : "#0078D7",
                }}
            >
                <Icon iconName={loading ? "Stop" : "Send"} />
            </button>
        </div>
    );
};

export default ChatInput;
